import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Row, Col, Typography, Popconfirm, InputNumber, AutoComplete, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, YoutubeOutlined, LinkOutlined, DownloadOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { downloadCsv } from '../utils/exportCsv';

const { Title, Text } = Typography;
const { Option } = Select;

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthLabel = (ym) => { const [y, m] = String(ym).split('-'); return `${(monthNames[Number(m) - 1] || '').slice(0, 3)} ${y}`; };
const usd = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Channels({ user }) {
  // Revenue is admin-only.
  const canSeeRevenue = user?.role === 'admin' || user?.role === 'super_admin';
  const [channels, setChannels] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCompany, setFilterCompany] = useState('');
  // '' = latest CMS month, 'YYYY-MM' = one month, 'year:YYYY' = month-wise columns for that year
  const [period, setPeriod] = useState('');
  const [revMonths, setRevMonths] = useState([]);
  const [shownMonths, setShownMonths] = useState([]);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCompanies();
    if (canSeeRevenue) api.get('/channels/revenue-months').then(r => setRevMonths(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchChannels(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCompany, period]);

  const periodParams = () => {
    if (period.startsWith('year:')) return { year: period.slice(5) };
    return period ? { month: period } : {};
  };

  const fetchCompanies = async () => {
    try { const res = await api.get('/companies'); setCompanies(res.data); } catch (err) { console.error(err); }
  };

  const fetchChannels = async () => {
    setLoading(true);
    try {
      const params = canSeeRevenue ? { revenue: 1, ...periodParams() } : {};
      if (filterCompany) params.company_id = filterCompany;
      const res = await api.get('/channels', { params });
      if (Array.isArray(res.data)) { setChannels(res.data); setShownMonths([]); }
      else { setChannels(res.data?.channels || []); setShownMonths(res.data?.months || []); }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSave = async (values) => {
    try {
      if (editing) {
        await api.put(`/channels/${editing.id}`, values);
        message.success('Channel updated!');
      } else {
        await api.post('/channels', values);
        message.success('Channel added!');
      }
      setModalVisible(false);
      setEditing(null);
      form.resetFields();
      fetchChannels();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/channels/${id}`);
      message.success('Channel deleted');
      fetchChannels();
    } catch (err) { message.error(err.response?.data?.error || 'Delete failed'); }
  };

  const columns = [
    { title: 'Channel Name', dataIndex: 'channel_name', key: 'name', render: (t, r) => <><YoutubeOutlined style={{ color: '#ff0000', marginRight: 8 }} />{t}{canSeeRevenue && r.in_cms === false && <Tag color="orange" style={{ marginLeft: 6 }}>CMS me nahi</Tag>}</> },
    { title: 'Channel ID', dataIndex: 'channel_id', key: 'cid', render: (v) => v || '-' },
    {
      title: 'URL', dataIndex: 'channel_url', key: 'url',
      render: (v) => v ? <a href={v} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a> : '-'
    },
    { title: 'Subscribers', dataIndex: 'subscriber_count', key: 'subs', render: (v) => (v || 0).toLocaleString('en-IN') },
    ...(canSeeRevenue ? [
      ...shownMonths.map(m => ({
        title: `${monthLabel(m)} ($)`, key: `rev_${m}`,
        render: (_, r) => <Text style={{ color: '#1677ff' }}>{usd(r.revenue_months?.[m])}</Text>,
        sorter: (a, b) => (Number(a.revenue_months?.[m]) || 0) - (Number(b.revenue_months?.[m]) || 0)
      })),
      ...(shownMonths.length > 1 ? [{
        title: 'Total ($)', dataIndex: 'revenue_usd', key: 'rev_total',
        render: (v) => <Text strong style={{ color: '#1677ff' }}>{usd(v)}</Text>,
        sorter: (a, b) => (Number(a.revenue_usd) || 0) - (Number(b.revenue_usd) || 0)
      }] : []),
    ] : []),
    { title: 'Company', dataIndex: 'company_name', key: 'company' },
    { title: 'Vendor', dataIndex: 'vendor', key: 'vendor', render: (v) => v || '-' },
    {
      title: 'CMS', dataIndex: 'cms_name', key: 'cms_name', render: (v) => v ? <Tag color="geekblue">{v}</Tag> : '-',
      filters: [...new Set(channels.map(c => c.cms_name).filter(Boolean))].map(v => ({ text: v, value: v })),
      onFilter: (v, r) => r.cms_name === v
    },
    {
      title: 'Actions', key: 'actions', width: 100,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); form.setFieldsValue(r); setModalVisible(true); }} />
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={3} style={{ margin: 0 }}><YoutubeOutlined style={{ color: '#ff0000' }} /> Bainsla Music Channels</Title></Col>
        <Col>
          <Space>
            <Input.Search placeholder="Search channel name" allowClear value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
            <Select placeholder="Filter by Company" value={filterCompany || undefined} onChange={(v) => setFilterCompany(v || '')} allowClear style={{ width: 180 }}>
              {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>
            {canSeeRevenue && (
              <Select value={period} onChange={(v) => setPeriod(v ?? '')} style={{ width: 210 }}>
                <Option value="">Latest month{revMonths[0] ? ` (${monthLabel(revMonths[0])})` : ''}</Option>
                {[...new Set(revMonths.map(m => m.slice(0, 4)))].map(y => <Option key={`y${y}`} value={`year:${y}`}>Month-wise: poora {y}</Option>)}
                {revMonths.map(m => <Option key={m} value={m}>{monthLabel(m)}</Option>)}
              </Select>
            )}
            {canSeeRevenue && <Button icon={<DownloadOutlined />} onClick={() => downloadCsv('/channels/export', { company_id: filterCompany, ...periodParams() })}>Download Excel</Button>}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); form.resetFields(); setModalVisible(true); }}>Add Channel</Button>
          </Space>
        </Col>
      </Row>

      <Table dataSource={search.trim() ? channels.filter(c => (c.channel_name || '').toLowerCase().includes(search.trim().toLowerCase())) : channels} columns={columns} rowKey="id" loading={loading} scroll={{ x: 'max-content' }}
        summary={canSeeRevenue && shownMonths.length ? (data) => {
          const revCols = [...shownMonths, ...(shownMonths.length > 1 ? ['total'] : [])];
          const sum = (k) => data.reduce((s, r) => s + (Number(k === 'total' ? r.revenue_usd : r.revenue_months?.[k]) || 0), 0);
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}><Text strong>TOTAL REVENUE (CMS)</Text></Table.Summary.Cell>
              {revCols.map((k, i) => (
                <Table.Summary.Cell key={k} index={4 + i}><Text strong style={{ color: '#1677ff' }}>{usd(sum(k))}</Text></Table.Summary.Cell>
              ))}
              <Table.Summary.Cell index={4 + revCols.length} colSpan={4} />
            </Table.Summary.Row>
          );
        } : undefined}
      />

      <Modal title={editing ? 'Edit Channel' : 'Add Channel'} open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditing(null); }} onOk={() => form.submit()}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="channel_name" label="Channel Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="channel_id" label="Channel ID"><Input placeholder="UC..." /></Form.Item>
          <Form.Item name="channel_url" label="Channel URL"><Input placeholder="https://youtube.com/@..." /></Form.Item>
          <Form.Item name="subscriber_count" label="Subscribers"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="vendor" label="Vendor (kiske paas channel / payment kisko)"><Input placeholder="Vendor / channel holder ka naam" /></Form.Item>
          <Form.Item name="cms_name" label="CMS (channel kis CMS me hai)">
            <AutoComplete allowClear placeholder="e.g. WMG" filterOption={(i, o) => (o?.value || '').toLowerCase().includes(i.toLowerCase())}
              options={[...new Set(channels.map(c => c.cms_name).filter(Boolean))].map(v => ({ value: v }))} />
          </Form.Item>
          <Form.Item name="company_id" label="Company" rules={[{ required: true }]}>
            <Select>{companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}</Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
